import { Component, ElementRef, OnDestroy, afterNextRender, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { gsap } from '../../../../../motion/gsap';
import { QUERIES } from '../../../../../motion/motion-env';
import { MotionService } from '../../../../../motion/motion.service';
import { langLink } from '../../../../../services/i18n-live';

@Component({
  selector: 'app-home-about',
  templateUrl: './home-about.component.html',
  styleUrl: './home-about.component.css',
})
export class HomeAboutSectionComponent implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly motion = inject(MotionService);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private destroyed = false;
  private revealed = false;

  constructor() {
    afterNextRender(() => this.motion.whenNeeded(this.host, () => this.destroyed || this.setupPhotoReveal()));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
  }

  link(route: string): string {
    return langLink(this.translate, route);
  }

  /** A single soft entrance, with a small lift and a gentle settling of the photo. */
  private setupPhotoReveal(): void {
    const frame = this.host.querySelector<HTMLElement>('.story-frame');
    const img = frame?.querySelector<HTMLImageElement>('img');
    if (!frame || !img) return;

    const mm = (this.mm = gsap.matchMedia(this.host));
    mm.add({ motion: QUERIES.motion, mobile: QUERIES.mobile }, (ctx) => {
      const conditions = ctx.conditions!;
      if (!conditions['motion'] || this.revealed) return;

      // Already scrolled past, or the page was rebuilt by a language switch: show it as is.
      const rect = frame.getBoundingClientRect();
      if (rect.bottom <= 0 || (this.motion.recentLanguageSwitch() && rect.top < window.innerHeight)) {
        this.revealed = true;
        return;
      }

      const mobile = !!conditions['mobile'];
      gsap
        .timeline({
          scrollTrigger: {
            trigger: frame,
            start: 'top 85%',
            once: true,
            onEnter: () => {
              this.revealed = true;
            },
          },
        })
        .fromTo(
          frame,
          { opacity: 0, y: mobile ? 10 : 16 },
          { opacity: 1, y: 0, duration: 0.75, ease: 'power2.out', clearProps: 'opacity,transform' },
          0
        )
        .fromTo(
          img,
          { scale: 1.04 },
          { scale: 1, duration: mobile ? 0.8 : 0.95, ease: 'power2.out', clearProps: 'transform' },
          0
        );

      // Keep the photo visible when motion preferences or the viewport change mid-reveal.
      return () => {
        gsap.set(frame, { clearProps: 'opacity,transform' });
        gsap.set(img, { clearProps: 'transform' });
      };
    });
  }
}
